import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { Filter, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "../lib/download";
import AttendanceTable from "./AttendanceTable";
import OverrideRequestsPanel from "./OverrideRequestsPanel";
import OverrideAuditReport from "./OverrideAuditReport";
import MonthlyAttendanceReport from "./MonthlyAttendanceReport";
import logger from "../lib/logger";

export default function AttendanceLog({ workers, sites }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workerFilter, setWorkerFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (workerFilter) params.worker_id = workerFilter;
    if (siteFilter) params.site_id = siteFilter;
    if (startDate) params.start = `${startDate}T00:00:00`;
    if (endDate) params.end = `${endDate}T23:59:59.999`;
    try {
      const { data } = await api.get("/clock/all", { params });
      setEvents(data);
    } catch (err) {
      logger.error("Attendance log fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [workerFilter, siteFilter, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const onExportCsv = useCallback(async () => {
    const params = {};
    if (workerFilter) params.worker_id = workerFilter;
    if (siteFilter) params.site_id = siteFilter;
    if (startDate) params.start = `${startDate}T00:00:00`;
    if (endDate) params.end = `${endDate}T23:59:59.999`;
    try {
      await downloadCsv("/clock/all.csv", params, "attendance.csv");
      toast.success("Attendance CSV downloaded");
    } catch (err) {
      logger.error("CSV download failed", err);
      toast.error("Could not download CSV");
    }
  }, [workerFilter, siteFilter, startDate, endDate]);

  const totals = useMemo(() => {
    const closed = events.filter((e) => e.clock_out);
    const totalMin = closed.reduce((acc, e) => acc + (e.duration_minutes || 0), 0);
    return {
      open: events.length - closed.length,
      closed: closed.length,
      totalHours: (totalMin / 60).toFixed(1),
    };
  }, [events]);

  return (
    <div className="space-y-5" data-testid="attendance-log">
      <OverrideRequestsPanel />

      <div className="grid grid-cols-3 gap-px bg-gray-200 border border-gray-300">
        <Stat label="Records" value={events.length} />
        <Stat label="Open now" value={totals.open} />
        <Stat label="Total hours" value={totals.totalHours} />
      </div>

      <FilterBar
        workers={workers}
        sites={sites}
        workerFilter={workerFilter}
        siteFilter={siteFilter}
        setWorkerFilter={setWorkerFilter}
        setSiteFilter={setSiteFilter}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        onExportCsv={onExportCsv}
      />

      <AttendanceTable events={events} loading={loading} />

      <MonthlyAttendanceReport
        onDrillDown={(siteId, year, month) => {
          setSiteFilter(siteId);
          if (year && month) {
            const s = `${year}-${String(month).padStart(2, "0")}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            const e = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
            setStartDate(s);
            setEndDate(e);
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />

      <OverrideAuditReport />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function FilterBar({ workers, sites, workerFilter, siteFilter, setWorkerFilter, setSiteFilter, startDate, endDate, setStartDate, setEndDate, onExportCsv }) {
  const showClear = workerFilter || siteFilter || startDate || endDate;
  return (
    <div className="border border-gray-300 bg-white p-4 rounded-sm flex flex-wrap items-end gap-3">
      <Filter className="w-4 h-4 text-gray-400 mb-2" />
      <FilterSelect
        label="Team Member"
        testId="attendance-worker-filter"
        value={workerFilter}
        onChange={setWorkerFilter}
        placeholder="All team members"
        items={workers}
      />
      <FilterSelect
        label="Site"
        testId="attendance-site-filter"
        value={siteFilter}
        onChange={setSiteFilter}
        placeholder="All sites"
        items={sites}
      />
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">From</label>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
          data-testid="attendance-start-filter"
          className="border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">To</label>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
          data-testid="attendance-end-filter"
          className="border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono" />
      </div>
      {showClear && (
        <button
          onClick={() => { setWorkerFilter(""); setSiteFilter(""); setStartDate(""); setEndDate(""); }}
          className="border border-gray-300 px-3 py-2 rounded-sm text-sm hover:bg-gray-100"
          data-testid="attendance-clear-filters"
        >
          Clear filters
        </button>
      )}
      <button
        onClick={onExportCsv}
        data-testid="attendance-export-csv"
        className="ml-auto border border-gray-800 bg-gray-900 text-white px-3 py-2 rounded-sm text-sm font-semibold hover:bg-black flex items-center gap-1.5"
      >
        <Download className="w-3.5 h-3.5" /> Export CSV
      </button>
    </div>
  );
}

function FilterSelect({ label, testId, value, onChange, placeholder, items }) {
  return (
    <div className="min-w-[200px]">
      <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white text-sm"
      >
        <option value="">{placeholder}</option>
        {items.map((it) => (<option key={it.id} value={it.id}>{it.name}</option>))}
      </select>
    </div>
  );
}
